import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Separator } from '@nucleus-commerce/ui/components/separator';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { isEditable } from '../schemas/draft-order-rules';

import { BlockerList } from './blocker-list';

function Row({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 text-sm',
        emphasis && 'font-semibold',
      )}
    >
      <dt className={emphasis ? undefined : 'text-muted-foreground'}>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/** The backend's totals as they came back; nothing here is computed on the client. */
export function DraftTotalsCard({ draft }: { draft: DraftOrder }) {
  const { t } = useTranslation('draftOrders');
  const format = useFormat();
  const open = isEditable(draft.status);

  return (
    <Card className="gap-3" data-tour="draft-orders.detail.totals">
      <CardHeader>
        <CardTitle>{t('totals.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="flex flex-col gap-1.5" aria-label={t('totals.title')}>
          <Row label={t('totals.subtotal')} value={format.money(draft.subtotal)} />
          {draft.discountTotal.amount !== 0 ? (
            <Row
              label={
                draft.discount?.reason
                  ? t('totals.discountWith', { reason: draft.discount.reason })
                  : t('totals.discount')
              }
              value={`−${format.money(draft.discountTotal)}`}
            />
          ) : null}
          <Row
            label={
              draft.shipping
                ? t('totals.shippingWith', { method: draft.shipping.title })
                : t('totals.shipping')
            }
            value={format.money(draft.shippingTotal)}
          />
          <Row label={t('totals.tax')} value={format.money(draft.tax)} />
          <Separator className="my-1" />
          <Row label={t('totals.total')} value={format.money(draft.total)} emphasis />
        </dl>
        {open ? (
          draft.blockers.length > 0 ? (
            <div className="bg-warning/10 border-warning/40 rounded-md border p-3" role="status">
              <p className="mb-1.5 text-sm font-medium">{t('blockers.title')}</p>
              <BlockerList blockers={draft.blockers} />
            </div>
          ) : (
            <p className="text-success flex items-center gap-1.5 text-sm" role="status">
              <CheckCircle2 className="size-4" aria-hidden />
              {draft.invoiceBlockers.includes('missing_email')
                ? t('totals.readyWithoutEmail')
                : t('totals.ready')}
            </p>
          )
        ) : null}
        <p className="text-muted-foreground text-xs">{t('totals.repriced')}</p>
      </CardContent>
    </Card>
  );
}
